import {usePortal} from '@common-web/providers/portalProvider';
import {trpc} from '@common-web/lib/trpc';
import {useClientFeatures} from '@common-web/hooks/useClientFeatures';
import type {InternalFeaturesShape} from '@common/menuFlags';
import {resolveAccountsViewConfig, type AccountsViewConfig} from '@common/accountsView';

/**
 * Resolve the active Accounts-view config for the current portal.
 *
 * - Client / broker / group portals use the tenant's `Client.features.accountsView`.
 * - The internal portal uses the org-wide `InternalConfig.features.accountsView`
 *   (the internal pipeline is intentionally distinct from any single client's).
 *
 * Falls back to the default 5-stage pipeline when nothing is configured.
 */
export function useAccountsViewConfig(): AccountsViewConfig {
  const portal = usePortal();
  const isInternal = portal.portalType === 'internal';

  const clientFeatures = useClientFeatures();

  // Only fires in the internal portal; client portals read from the session.
  const internalConfig = trpc.internal.config.get.useQuery(undefined, {
    enabled: isInternal,
    staleTime: 60_000,
  });

  const accountsView = isInternal
    ? ((internalConfig.data?.features ?? {}) as InternalFeaturesShape).accountsView
    : clientFeatures.accountsView;

  return resolveAccountsViewConfig(accountsView, isInternal ? 'internal' : 'client');
}
